import { LeftOutlined } from "@ant-design/icons"
import { Button, Flex, Skeleton, Space, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { Link } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import { NotAllowed } from "../../../components/PermissionGate/NotAllowed.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import {
  fetchAvailableBlocks,
  fetchTemplateById,
  previewTemplate,
  updateTemplate
} from "../redux/thunks/document-templates.thunks.ts"
import {
  type DocumentTemplate,
  type DocumentTemplateMutateData
} from "../types/types.ts"
import { DocumentTemplateForm } from "./components/DocumentTemplateForm.tsx"

const { Title, Text } = Typography

function DocumentTemplateEditPage({
  availableBlocks,
  fetching,
  getBlocks,
  getTemplate,
  updateTemplate,
  previewTemplate
}: ConnectedProps<typeof connector>) {
  const id = useParams()?.id as string
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [template, setTemplate] = useState<DocumentTemplate | null>(null)

  const handleGetTemplate = useCallback(async () => {
    try {
      setLoading(true)
      const res = await getTemplate(id).unwrap()
      if (res) setTemplate(res)
    } catch (e) {
      console.log(e)
    } finally {
      setLoading(false)
    }
  }, [getTemplate, id])

  const handleSave = useCallback(
    async (data: DocumentTemplateMutateData) => {
      try {
        await updateTemplate({
          id,
          // to remove field from payload
          data: { ...data, project_type: undefined }
        }).unwrap()
        navigate(RoutesPaths.settings.documentTemplates)
      } catch (e) {
        console.log(e)
      }
    },
    [updateTemplate, id, navigate]
  )

  const handlePreview = useCallback(
    async (payload: DocumentTemplateMutateData) => {
      return await previewTemplate(payload).unwrap()
    },
    [previewTemplate]
  )

  useEffect(() => {
    getBlocks()
  }, [getBlocks])

  useEffect(() => {
    void handleGetTemplate()
  }, [handleGetTemplate])

  if (fetching || loading)
    return <Skeleton.Node active className={"size-full"} />

  if (!loading && !template) return <div>Template not found</div>

  if (!template?.removable) {
    return (
      <NotAllowed
        title="Default Template"
        description="This is a system default template and cannot be modified or removed."
      />
    )
  }

  return (
    <FixedContainer>
      <Flex justify={"space-between"}>
        <Space>
          <Flex vertical>
            <Title level={4}>Edit Template</Title>
            <Text type="secondary">
              Edit your proposal and contract templates
            </Text>
          </Flex>
        </Space>
        <Link to={RoutesPaths.settings.documentTemplates}>
          <Button type="primary" icon={<LeftOutlined />}>
            Templates List
          </Button>
        </Link>
      </Flex>
      {template && (
        <DocumentTemplateForm
          availableBlocks={availableBlocks}
          initialValues={template}
          onSubmit={handleSave}
          onPreview={handlePreview}
        />
      )}
    </FixedContainer>
  )
}

const mapStateToProps = (state: RootState) => ({
  availableBlocks: state.documentTemplates.availableBlocks || [],
  fetching: state.documentTemplates.fetching
})

const mapDispatchToProps = {
  getBlocks: fetchAvailableBlocks,
  getTemplate: fetchTemplateById,
  updateTemplate,
  previewTemplate
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(DocumentTemplateEditPage)
